/**
 * Shared docker/podman deploy target, parameterized by binary name.
 *
 * docker and podman expose the same CLI surface for the run/inspect/logs/rm
 * verbs this target needs, so one implementation backs both
 * (`makeContainerDeployTarget("docker" | "podman")`). `tlsTermination: "none"`
 * means loopback-only: a zod refinement rejects a non-loopback `bind` until tls
 * config lands (locked decision #12).
 */

import { type PortableSubprocess, portableSpawn, portableSpawnSync } from "@skaile/workspaces/core";
import {
  type DeployContext,
  type DeployHandle,
  type DeployTarget,
  makeDeployHandle,
} from "@skaile/workspaces/plugin-registry";
import * as z from "zod";
import { allocateFreePort } from "./port.js";
import { LineBuffer, pumpLines, waitForLine } from "./stream-lines.js";

type ContainerBin = "docker" | "podman";

const LOOPBACK_BINDS = new Set(["127.0.0.1", "localhost", "::1"]);
const DEFAULT_READY_PATTERN = "ws://";
const DEFAULT_READY_TIMEOUT_MS = 60_000;
/**
 * Deadline for `<bin> run -d`. Generous because it covers an image pull on a
 * cold host; the flag still bounds a runtime that has itself wedged.
 */
const CONTAINER_RUN_TIMEOUT_MS = 300_000;
/** Deadline for `<bin> rm -f` during teardown — a local, metadata-only call. */
const CONTAINER_RM_TIMEOUT_MS = 30_000;

const containerConfigSchema = z
  .object({
    image: z.string(),
    port: z.number().int().positive().optional(),
    bind: z.string().default("127.0.0.1"),
    mounts: z.array(z.string()).default([]),
    env: z.record(z.string(), z.string()).default({}),
    network: z.string().optional(),
    pullPolicy: z.enum(["always", "if-missing", "never"]).default("if-missing"),
    // "local" only — this target runs a prebuilt image and has no in-provider builder.
    // The full buildStrategySchema (local/remote/managed) lives in plugin-registry for the provider-* packages that implement remote builds.
    buildStrategy: z.literal("local").default("local"),
    containerPort: z.number().int().positive().optional(),
    readyPattern: z.string().optional(),
  })
  .strict()
  .refine((c) => LOOPBACK_BINDS.has(c.bind), {
    message: "non-loopback bind requires tls config (not yet supported); use a loopback bind",
    path: ["bind"],
  });

type ContainerConfig = z.infer<typeof containerConfigSchema>;

function payloadSchemaFor(bin: ContainerBin) {
  return z.object({
    containerId: z.string(),
    hostPort: z.number(),
    runtime: z.enum(["docker", "podman"]).refine((r) => r === bin, {
      message: `runtime mismatch: handle belongs to ${bin}`,
    }),
    // Older handles predate `bind`; default to loopback so restore stays valid.
    bind: z.string().default("127.0.0.1"),
  });
}

type ContainerPayload = {
  containerId: string;
  hostPort: number;
  runtime: ContainerBin;
  bind: string;
};

function pullFlag(policy: ContainerConfig["pullPolicy"]): string[] {
  // `docker run --pull` takes always|missing|never; map our "if-missing".
  const mapped = policy === "if-missing" ? "missing" : policy;
  return ["--pull", mapped];
}

async function readStream(stream: ReadableStream<Uint8Array>): Promise<string> {
  const reader = stream.getReader();
  const decoder = new TextDecoder();
  let out = "";
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      if (value) out += decoder.decode(value, { stream: true });
    }
  } finally {
    reader.releaseLock();
  }
  return out + decoder.decode();
}

/**
 * True when `<bin> inspect` reports the container in a running state.
 *
 * Async (over `portableSpawn` + `await exited`) so the inspect round-trip stays
 * off the event loop — `health()` / `restore()` poll this without freezing log
 * pumps or other connections.
 */
async function isRunning(bin: ContainerBin, containerId: string): Promise<boolean> {
  const proc = portableSpawn([bin, "inspect", "-f", "{{.State.Running}}", containerId]);
  const [exitCode, stdout] = await Promise.all([proc.exited, readStream(proc.stdout)]);
  return exitCode === 0 && stdout.trim() === "true";
}

/** The single `<bin> logs --follow` stream that backs both readiness and `logs()`. */
interface LogStream {
  buffer: LineBuffer;
  proc: PortableSubprocess;
}

/**
 * Spawn one shared `<bin> logs --follow` and pump it into a single
 * {@link LineBuffer}. Both `waitReady` (readiness marker) and `logs()` (replay +
 * tail) read from this one buffer, so the stream is decoded/split exactly once.
 */
function startLogStream(bin: ContainerBin, containerId: string): LogStream {
  const buffer = new LineBuffer();
  const proc = portableSpawn([bin, "logs", "--follow", containerId]);
  void pumpLines(proc.stdout, buffer);
  void pumpLines(proc.stderr, buffer);
  return { buffer, proc };
}

/**
 * Build the `DeployHandle` for a container via the shared {@link makeDeployHandle}
 * factory. The factory drives the state transitions; the seams here only do the
 * container-specific work (inspect for health, follow `logs`, `rm -f` on stop).
 */
function buildHandle(
  bin: ContainerBin,
  payload: ContainerPayload,
  config: Pick<ContainerConfig, "bind" | "readyPattern">,
  ctx: DeployContext,
  logStream: LogStream,
  ready: (timeoutMs: number, signal: AbortSignal) => Promise<void>,
): DeployHandle<ContainerPayload> {
  return makeDeployHandle<ContainerPayload>({
    wsUrl: `ws://${config.bind}:${payload.hostPort}`,
    ctx,
    payload,
    initialState: "starting",
    defaultReadyTimeoutMs: DEFAULT_READY_TIMEOUT_MS,
    ready,
    async health() {
      const healthy = await isRunning(bin, payload.containerId);
      return healthy
        ? { healthy }
        : { healthy, detail: `container ${payload.containerId} not running` };
    },
    async *logs(opts) {
      // Read off the one shared follow stream — no second `<bin> logs` spawn.
      // `since` replays buffered history; follow then tails new pushes.
      const { buffer } = logStream;
      const since = opts?.sinceMs ?? 0;
      for (const l of buffer.since(since)) yield l;
      if (!opts?.follow) return;
      const queue: { ts: number; line: string }[] = [];
      let notify: (() => void) | null = null;
      const unsub = buffer.subscribe((l) => {
        queue.push(l);
        notify?.();
      });
      try {
        while (!ctx.signal.aborted) {
          if (queue.length === 0) {
            await new Promise<void>((r) => {
              notify = r;
            });
            notify = null;
          }
          while (queue.length > 0) yield queue.shift() as { ts: number; line: string };
        }
      } finally {
        // Detach this consumer only; the shared stream lives until stop().
        unsub();
      }
    },
    async stop() {
      logStream.proc.kill("SIGTERM");
      // `rm -f` removes a running or stopped container and is a no-op (nonzero
      // exit, ignored) when the container is already gone — idempotent.
      portableSpawnSync([bin, "rm", "-f", payload.containerId], {
        timeoutMs: CONTAINER_RM_TIMEOUT_MS,
      });
    },
  });
}

/** Build a docker/podman deploy target backed by `bin`. */
export function makeContainerDeployTarget(
  bin: ContainerBin,
): DeployTarget<ContainerConfig, ContainerPayload> {
  const payloadSchema = payloadSchemaFor(bin) as unknown as z.ZodType<ContainerPayload>;
  return {
    id: bin,
    displayName: bin === "docker" ? "Docker container" : "Podman container",
    apiVersion: 1,
    tlsTermination: "none",
    configSchema: containerConfigSchema as unknown as z.ZodType<ContainerConfig>,
    payloadSchema,

    async create(config, ctx) {
      const log = ctx.log;
      const hostPort = config.port ?? (await allocateFreePort(config.bind));
      const containerPort = config.containerPort ?? hostPort;
      const name = `skaile-ws-${ctx.workspaceId}`;
      const pattern = new RegExp(config.readyPattern ?? DEFAULT_READY_PATTERN);

      const args = [
        bin,
        "run",
        "-d",
        "--name",
        name,
        "-p",
        `${config.bind}:${hostPort}:${containerPort}`,
        ...pullFlag(config.pullPolicy),
      ];
      if (config.network) args.push("--network", config.network);
      // Generic per-session injection from the host (DeployContext). `config.*`
      // is the target's own static policy and wins over the generic channel.
      for (const [k, v] of Object.entries(ctx.env)) args.push("-e", `${k}=${v}`);
      for (const [k, v] of Object.entries(ctx.labels ?? {})) args.push("--label", `${k}=${v}`);
      for (const m of ctx.mounts ?? []) {
        args.push("-v", `${m.host}:${m.container}${m.ro ? ":ro" : ""}`);
      }
      if (ctx.resources?.cpuNanos !== undefined) {
        args.push("--cpus", String(ctx.resources.cpuNanos / 1e9));
      }
      if (ctx.resources?.memoryBytes !== undefined) {
        args.push("--memory", String(ctx.resources.memoryBytes));
      }
      if (ctx.resources?.pidsLimit !== undefined) {
        args.push("--pids-limit", String(ctx.resources.pidsLimit));
      }
      for (const [k, v] of Object.entries(config.env)) args.push("-e", `${k}=${v}`);
      for (const m of config.mounts) args.push("-v", m);
      args.push(config.image);

      const runRes = portableSpawnSync(args, { timeoutMs: CONTAINER_RUN_TIMEOUT_MS });
      if (runRes.exitCode !== 0) {
        throw new Error(`${bin} run failed: ${runRes.stderr.trim() || runRes.stdout.trim()}`);
      }
      const containerId = runRes.stdout.trim();
      log.info("container started", { runtime: bin, containerId, hostPort });

      const payload: ContainerPayload = { containerId, hostPort, runtime: bin, bind: config.bind };
      // One shared follow stream feeds both readiness and logs() (see startLogStream).
      const logStream = startLogStream(bin, containerId);
      const ready = async (timeoutMs: number, signal: AbortSignal) => {
        await waitForLine(logStream.buffer, pattern, { timeoutMs, signal });
      };
      return buildHandle(bin, payload, config, ctx, logStream, ready);
    },

    async restore(payload, ctx) {
      const log = ctx.log;
      if (!(await isRunning(bin, payload.containerId))) {
        throw new Error(`${bin} container ${payload.containerId} is not running`);
      }
      log.info("re-attached to container", { runtime: bin, containerId: payload.containerId });
      const logStream = startLogStream(bin, payload.containerId);
      return buildHandle(
        bin,
        payload,
        { bind: payload.bind, readyPattern: undefined },
        ctx,
        logStream,
        async () => {},
      );
    },
  };
}
